// 📋 THE DEALS BOARD — Sir Eric's 30-year dream. The local advertisement wall above the map:
// billboard rectangles with carousel ads, pinch to resize, tap to go fullscreen, BIZ ↔ SPORTS modes.
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { X, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { logoFor } from "@/lib/brandLogos";
import { MerchantSquare } from "@/components/MerchantSquare";
import { PogTicker } from "@/components/PogTicker";
import { useConsumerAuth } from "@/consumer/ConsumerAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// 🎛️ SHUFFLE SPEED — POG'R-controlled ad-flip pace (persisted)
const SPEEDS = [
  { ms: 8000, label: "🐢 SLOW" },
  { ms: 5000, label: "🚶 CHILL" },
  { ms: 2500, label: "⚡ FAST" },
];

const AdCarousel = ({ ads, big, speedMs = 5000 }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (ads.length < 2) return;
    const t = setInterval(() => setI((n) => (n + 1) % ads.length), speedMs);
    return () => clearInterval(t);
  }, [ads.length, speedMs]);
  const ad = ads[i] || {};
  return (
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center px-3 overflow-hidden">
      {ad.discount && <p className={`font-heading font-black text-amber-300 ${big ? "text-5xl" : "text-xl"}`}>{ad.discount}</p>}
      <p className={`font-heading font-extrabold text-white leading-tight ${big ? "text-2xl mt-2" : "text-[12px] line-clamp-2"}`}>{ad.title}</p>
      {ad.detail && <p className={`text-slate-400 font-semibold ${big ? "text-sm mt-2" : "text-[9px] line-clamp-1"}`}>{ad.detail}</p>}
      {ads.length > 1 && (
        <div className="flex gap-1 mt-1.5">{ads.map((_, n) => (
          <span key={n} className={`rounded-full ${big ? "w-2 h-2" : "w-1 h-1"} ${n === i ? "bg-amber-400" : "bg-slate-600"}`} />
        ))}</div>
      )}
    </div>
  );
};

const BoardCard = ({ b, onExpand, speedMs }) => {
  const logo = logoFor(b, null, BACKEND);
  return (
    <button data-testid={`board-card-${b.business_id}`} onClick={() => onExpand(b)}
      className={`relative rounded-2xl overflow-hidden flex flex-col aspect-[4/3] text-left transition-colors ${b.prime
        ? "bg-slate-900 border-2 border-amber-400/80 shadow-lg shadow-amber-500/20"
        : "bg-slate-900/90 border border-violet-500/30 hover:border-amber-400/60"}`}>
      {b.prime && <span className="absolute top-1 right-1.5 text-[8px] font-black tracking-widest text-amber-300">👑 PRIME</span>}
      <div className="px-2 pt-1.5 flex items-center justify-between gap-1">
        <p className="text-[9px] font-black tracking-wide text-violet-300 truncate">{b.name}</p>
        {!b.prime && <Maximize2 className="w-3 h-3 text-slate-600 shrink-0" />}
      </div>
      <AdCarousel ads={b.ads} speedMs={speedMs} />
      {/* Bottom-left: merchant logo then POG'ODEX symbols (per the dream) */}
      <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1">
        <span className="w-6 h-6 rounded-md bg-white flex items-center justify-center overflow-hidden shrink-0">
          {logo ? <img src={logo} alt="" className="w-5 h-5 object-contain" /> : <span className="text-[10px]">🏪</span>}
        </span>
        {b.badges.slice(0, 4).map((s, n) => <span key={n} className="text-[11px]">{s}</span>)}
      </div>
      {b.category_label && <span className="absolute bottom-1.5 right-2 text-[8px] font-bold text-slate-500 truncate max-w-[40%]">{b.category_label}</span>}
    </button>
  );
};

const MysteryCard = ({ driver }) => {
  const [state, setState] = useState("hidden"); // hidden | opening | revealed
  const [deal, setDeal] = useState(null);
  const openIt = () => {
    if (!driver) { toast("Sign in to open the Mystery Deal! 🎩"); return; }
    if (state !== "hidden") return;
    setState("opening");
    axios.post(`${API}/deals-board/mystery`, {}, { withCredentials: true })
      .then((r) => {
        setDeal(r.data.deal); setState("revealed");
        if (r.data.bucks > 0) toast.success(`❓ MYSTERY OPENED — +${r.data.bucks} POG BUCKS! 🎯`);
      })
      .catch((e) => { toast.error(e.response?.data?.detail || "The vault jammed — try again!"); setState("hidden"); });
  };
  return (
    <button data-testid="board-mystery-card" onClick={openIt}
      className="relative rounded-2xl overflow-hidden flex flex-col items-center justify-center aspect-[4/3] bg-gradient-to-br from-fuchsia-600/30 to-violet-700/30 border-2 border-dashed border-fuchsia-400/60">
      {state === "revealed" && deal ? (
        <div className="text-center px-2">
          <p className="text-[9px] font-black text-fuchsia-300 truncate">{deal.business_name}</p>
          {deal.discount && <p className="font-heading font-black text-xl text-amber-300">{deal.discount}</p>}
          <p className="text-[11px] font-heading font-extrabold text-white leading-tight line-clamp-2">{deal.title}</p>
        </div>
      ) : (
        <>
          <span className={`text-4xl ${state === "opening" ? "animate-spin" : "animate-pulse"}`}>❓</span>
          <p className="text-[9px] font-black tracking-widest text-fuchsia-300 mt-1">MYSTERY DEAL</p>
          <p className="text-[8px] font-bold text-slate-400">tap to reveal · daily POG BUCKS</p>
        </>
      )}
    </button>
  );
};

const RentSlotCard = ({ driver }) => {
  const [form, setForm] = useState(false);
  const [headline, setHeadline] = useState("");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const pay = () => {
    if (headline.trim().length < 2) { toast.error("Give your space a headline!"); return; }
    setBusy(true);
    axios.post(`${API}/deals-board/slot-checkout`,
      { headline, detail, name: "", origin_url: window.location.origin }, { withCredentials: true })
      .then((r) => { window.location.href = r.data.url; })
      .catch((e) => { toast.error(e.response?.data?.detail || "Rental didn't start!"); setBusy(false); });
  };
  return (
    <div data-testid="board-rent-card"
      className="relative rounded-2xl overflow-hidden flex flex-col items-center justify-center aspect-[4/3] bg-slate-900/60 border-2 border-dashed border-emerald-400/50 px-2">
      {form ? (
        <div className="w-full space-y-1">
          <input data-testid="rent-headline" value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={60}
            placeholder="Your headline…" className="w-full h-7 rounded-lg bg-slate-800 border border-slate-700 px-2 text-[10px] text-white outline-none" />
          <input data-testid="rent-detail" value={detail} onChange={(e) => setDetail(e.target.value)} maxLength={120}
            placeholder="Details (optional)" className="w-full h-7 rounded-lg bg-slate-800 border border-slate-700 px-2 text-[10px] text-white outline-none" />
          <button data-testid="rent-pay" onClick={pay} disabled={busy}
            className="w-full h-8 rounded-lg bg-emerald-500 text-slate-900 text-[10px] font-black disabled:opacity-60">
            {busy ? "…" : "PAY $5 — 7 DAYS ON THE WALL 📐"}
          </button>
        </div>
      ) : (
        <button onClick={() => (driver ? setForm(true) : toast("Sign in to rent this space! 🎩"))} className="text-center">
          <span className="text-3xl">📐</span>
          <p className="text-[9px] font-black tracking-widest text-emerald-300 mt-1">THIS SPACE FOR RENT</p>
          <p className="text-[8px] font-bold text-slate-400">🏷 garage sales · 🚚 food trucks · 🚐 mobile biz</p>
          <p className="text-[8px] font-black text-emerald-400">$5 · 7 days · anyone can post</p>
        </button>
      )}
    </div>
  );
};

const TravelCard = () => (
  <a data-testid="board-travel-card" href="/travel"
    className="relative rounded-2xl overflow-hidden flex flex-col items-center justify-center aspect-[4/3] bg-gradient-to-br from-sky-600/30 to-indigo-700/30 border-2 border-sky-400/60 px-2 text-center">
    <span className="text-3xl">✈️</span>
    <p className="text-[9px] font-black tracking-widest text-sky-300 mt-1">CATCH A POG FLIGHT</p>
    <p className="text-[8px] font-bold text-slate-400">hotels · flights · vacations · rides</p>
    <p className="text-[8px] font-black text-amber-300">+5 POG BUCKS · plan a trip</p>
  </a>
);

const TRAVEL_WALL = [
  { id: "flight", emoji: "✈️", title: "POG FLIGHT", sub: "catch a flight — search prepped" },
  { id: "stay", emoji: "🏨", title: "POG STAY", sub: "book a hotel now" },
  { id: "cruise", emoji: "🚢", title: "POG CRUISE", sub: "floating resorts at sea" },
  { id: "escape", emoji: "🌴", title: "POG ESCAPE", sub: "full vacation packages" },
  { id: "ride", emoji: "🚗", title: "POG RIDE", sub: "doorstep to doorstep" },
  { id: "countdown", emoji: "🗓", title: "TRIP COUNTDOWN", sub: "count down your next adventure" },
];

const TravelWall = ({ cols }) => {
  const [trending, setTrending] = useState([]);
  useEffect(() => {
    axios.get(`${API}/travel/trending`).then((r) => setTrending(r.data.trending || [])).catch(() => {});
  }, []);
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
      {trending.map((t) => (
        <a key={t.dest} data-testid={`board-trending-${t.dest}`} href="/travel"
          className="relative rounded-2xl overflow-hidden flex flex-col items-center justify-center aspect-[4/3] bg-gradient-to-br from-amber-600/25 to-orange-700/25 border border-amber-400/60 px-2 text-center hover:border-amber-300 transition-colors">
          <span className="text-2xl">🔥</span>
          <p className="text-[10px] font-black tracking-wide text-amber-300 mt-1 truncate max-w-full">{t.dest}</p>
          <p className="text-[8px] font-bold text-slate-400">{t.plans} POG'R{t.plans === 1 ? "" : "S"} planning this week</p>
          <p className="text-[8px] font-black text-sky-300">TRENDING DESTINATION</p>
        </a>
      ))}
      {TRAVEL_WALL.map((w) => (
        <a key={w.id} data-testid={`board-travel-${w.id}`} href="/travel"
          className="relative rounded-2xl overflow-hidden flex flex-col items-center justify-center aspect-[4/3] bg-gradient-to-br from-sky-600/25 to-indigo-700/25 border border-sky-400/50 px-2 text-center hover:border-sky-300 transition-colors">
          <span className="text-3xl">{w.emoji}</span>
          <p className="text-[9px] font-black tracking-widest text-sky-300 mt-1">{w.title}</p>
          <p className="text-[8px] font-bold text-slate-400">{w.sub}</p>
          <p className="text-[8px] font-black text-amber-300">+5 POG BUCKS · plan a trip</p>
        </a>
      ))}
    </div>
  );
};

const SportCard = ({ g }) => (
  <div data-testid={`board-sport-${g.id}`} className="rounded-2xl bg-slate-900/90 border border-emerald-500/30 overflow-hidden flex flex-col aspect-[4/3] p-2">
    <p className="text-[9px] font-black tracking-widest text-emerald-300">⚾ MLB · {g.detail}</p>
    <div className="flex-1 flex items-center justify-center gap-3">
      {[g.away, g.home].map((t, n) => (
        <div key={n} className="text-center">
          <p className="font-heading font-black text-lg text-white">{t.abbr}</p>
          <p className="font-heading font-black text-2xl text-amber-300">{t.score}</p>
        </div>
      ))}
    </div>
    <p className="text-[8px] font-bold text-slate-500 truncate">{g.away.name} @ {g.home.name}</p>
  </div>
);

export default function DealsBoardSheet({ open, onClose, businesses = [], onMerchantTap }) {
  const { driver, toggleFavorite } = useConsumerAuth() || {};
  const [mode, setMode] = useState("biz");
  const [cat, setCat] = useState("random");
  const [cats, setCats] = useState([]);
  const [boards, setBoards] = useState(null);
  const [games, setGames] = useState([]);
  const [cols, setCols] = useState(2);
  const [full, setFull] = useState(null);
  const [patch, setPatch] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [speedMs, setSpeedMs] = useState(() => parseInt(localStorage.getItem("airpog_board_speed"), 10) || 5000);
  const cycleSpeed = () => {
    const idx = SPEEDS.findIndex((s) => s.ms === speedMs);
    const next = SPEEDS[(idx + 1) % SPEEDS.length].ms;
    setSpeedMs(next);
    localStorage.setItem("airpog_board_speed", String(next));
  };
  const pinch = useRef(null);
  const startY = useRef(null);

  const shuffle = () => {
    if (spinning) return;
    setSpinning(true);
    axios.get(`${API}/deals-board?category=random`).then((r) => {
      setTimeout(() => { setBoards(r.data.boards); setSpinning(false); }, 650);
    }).catch(() => setSpinning(false));
  };

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/categories`).then((r) => setCats(r.data.categories || [])).catch(() => {});
  }, [open]);
  useEffect(() => {
    if (!open || !driver) return;
    axios.get(`${API}/deals-board/my-patch`, { withCredentials: true })
      .then((r) => setPatch(r.data.slots?.length ? r.data : null)).catch(() => {});
  }, [open, driver]);
  const goPrime = () => {
    axios.post(`${API}/deals-board/slot-prime-checkout`,
      { slot_id: patch.slots[0].id, origin_url: window.location.origin }, { withCredentials: true })
      .then((r) => { window.location.href = r.data.url; })
      .catch((e) => toast.error(e.response?.data?.detail || "PRIME upgrade didn't start!"));
  };
  const expand = (b) => {
    setFull(b);
    axios.post(`${API}/deals-board/track`, { board_id: b.business_id }).catch(() => {});
  };
  useEffect(() => {
    if (!open) return;
    if (mode === "biz") {
      setBoards(null);
      axios.get(`${API}/deals-board?category=${cat}`).then((r) => setBoards(r.data.boards)).catch(() => setBoards([]));
    } else if (mode === "sports") {
      axios.get(`${API}/sports/scores`).then((r) => setGames(r.data.games || [])).catch(() => setGames([]));
    }
  }, [open, mode, cat]);

  // 🧭 CATEGORY PIONEER — first scout of a freshly-minted category earns POG BUCKS
  useEffect(() => {
    if (!open || !driver || mode !== "biz" || !["health_medical", "local_leagues"].includes(cat)) return;
    axios.post(`${API}/category-scout/${cat}`, {}, { withCredentials: true })
      .then((r) => { if (r.data.awarded) toast.success(`🧭 CATEGORY PIONEER! +${r.data.bucks} POG BUCKS for scouting a fresh category`); })
      .catch(() => {});
  }, [open, mode, cat, driver]);

  if (!open) return null;
  // 🌊 THE BIG WELL — fill the wall with the same merchants the POG STRIP knows.
  const well = (() => {
    if (mode !== "biz" || !boards) return [];
    const taken = new Set(boards.map((x) => (x.name || "").toLowerCase().trim()));
    const pool = (businesses || []).filter((x) => x?.name && !taken.has(x.name.toLowerCase().trim()))
      .filter((x) => cat === "random" || x.category_id === cat);
    for (let i = pool.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [pool[i], pool[j]] = [pool[j], pool[i]]; }
    return pool.slice(0, Math.max(0, 60 - boards.length));
  })();
  const openWellSquare = (x) => {
    axios.post(`${API}/deals-board/track`, { board_id: `well_${String(x.id).slice(0, 70)}` }).catch(() => {});
    onClose?.();
    onMerchantTap?.(x);
  };
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  return (
    <div data-testid="deals-board-sheet" className="fixed inset-0 z-[1400] bg-gradient-to-b from-[#0b0420] via-[#160a33] to-[#0f172a] flex flex-col animate-in slide-in-from-top duration-300"
      onTouchStart={(e) => {
        if (e.touches.length === 2) pinch.current = dist(e.touches);
        else startY.current = e.touches[0].clientY;
      }}
      onTouchMove={(e) => {
        if (e.touches.length === 2 && pinch.current) {
          const d = dist(e.touches);
          if (d - pinch.current > 60) { setCols((c) => Math.max(1, c - 1)); pinch.current = d; }
          else if (pinch.current - d > 60) { setCols((c) => Math.min(3, c + 1)); pinch.current = d; }
        }
      }}
      onTouchEnd={() => { pinch.current = null; startY.current = null; }}
    >
      <header className="px-4 pt-[max(0.9rem,env(safe-area-inset-top))] pb-2 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-black text-lg text-white leading-none">📋 DEALS BOARD</h1>
            <p className="text-[9px] font-black tracking-widest text-violet-300 mt-1">LOCAL AD BOARDS</p>
            <a data-testid="board-legend-link" href="/comics" className="inline-block mt-1 text-[9px] font-black tracking-widest text-amber-300/90 hover:text-amber-200">⚡ READ THE LEGEND OF THE BOARD →</a>
            <a data-testid="board-story-link" href="/story" className="inline-block mt-1 ml-3 text-[9px] font-black tracking-widest text-yellow-300/90 hover:text-yellow-200">📖 30 YEARS IN THE MAKING — OUR STORY →</a>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="flex rounded-xl bg-slate-800/80 p-0.5">
              {[["biz", "🏪 BIZ"], ["travel", "✈️ TRAVEL"], ["sports", "🏟 SPORTS"]].map(([m, l]) => (
                <button key={m} data-testid={`board-mode-${m}`} onClick={() => setMode(m)}
                  className={`h-8 px-3 rounded-lg text-[10px] font-black ${mode === m ? "bg-amber-400 text-slate-900" : "text-slate-400"}`}>{l}</button>
              ))}
            </div>
            <button data-testid="board-zoom" onClick={() => setCols((c) => (c >= 3 ? 1 : c + 1))} title="Resize boards (or pinch!)"
              className="h-8 w-8 rounded-xl bg-slate-800/80 text-slate-300 text-xs font-black">{cols}×</button>
            <button data-testid="deals-board-close" onClick={onClose} className="h-8 w-8 rounded-xl bg-slate-800/80 flex items-center justify-center">
              <X className="w-4 h-4 text-slate-300" />
            </button>
          </div>
        </div>
        {mode === "biz" && (
          <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
            <button data-testid="board-cat-random" onClick={() => setCat("random")}
              className={`h-7 px-2.5 rounded-full text-[10px] font-black shrink-0 ${cat === "random" ? "bg-violet-500 text-white" : "bg-slate-800 text-slate-400"}`}>🎲 RANDOM</button>
            {cats.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)}
                className={`h-7 px-2.5 rounded-full text-[10px] font-black shrink-0 ${cat === c.id ? "bg-violet-500 text-white" : "bg-slate-800 text-slate-400"}`}>{c.label}</button>
            ))}
          </div>
        )}
      </header>
      {/* 📰 THE POG TICKER — the board's crawling marquee */}
      <div className="shrink-0 px-3 pb-2">
        <PogTicker businesses={businesses} mode={mode} onMerchant={(b) => { onClose?.(); onMerchantTap?.(b); }} />
      </div>
      <main className="flex-1 overflow-y-auto px-3 pb-8">
        {!driver && (
          <p data-testid="board-signin-note" className="mb-2 text-center text-[10px] font-bold text-slate-500">Free POG'R account unlocks tapping deals — browsing is on the house 🎩</p>
        )}
        {/* 🍋 LITTLE GIANTS — kid-run stands near you, front and center */}
        {mode === "biz" && (() => {
          const kids = (businesses || []).filter((b) => b.category_id === "personal_sale" && ["Lemonade Stand", "Bake Sale", "Kids Car Wash", "Craft Stand"].includes(b.sale_type)).slice(0, 10);
          if (!kids.length) return null;
          return (
            <div data-testid="little-giants-row" className="mb-2 rounded-xl bg-yellow-400/10 border border-yellow-300/40 p-2.5">
              <p className="text-[10px] font-heading font-black text-yellow-300 mb-1.5">🍋 LITTLE GIANTS — kid-run stands near you</p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {kids.map((x) => (
                  <button key={x.id} data-testid={`little-giant-${x.id}`} onClick={() => openWellSquare(x)}
                    className="shrink-0 rounded-lg bg-slate-900/70 border border-yellow-300/30 px-2.5 py-1.5 text-left active:scale-95 transition-transform flex items-center gap-2">
                    {x.ad_image_url && <img src={`${BACKEND}${x.ad_image_url}`} alt={x.name} loading="lazy" className="w-9 h-9 rounded-md object-cover border border-yellow-300/40" />}
                    <span>
                      <p className="text-[11px] font-heading font-black text-white whitespace-nowrap">{x.golden_lemon ? "👑 " : ""}{({ "Lemonade Stand": "🍋", "Bake Sale": "🧁", "Kids Car Wash": "🫧", "Craft Stand": "🎨" })[x.sale_type]} {x.name}</p>
                      <p className="text-[9px] font-bold text-yellow-200/80">
                        {x.golden_lemon ? "GOLDEN LEMON — busiest stand! · " : ""}{x.sale_type}
                        {(() => { const ms = x.sale_ends_at ? new Date(x.sale_ends_at) - Date.now() : 0; return ms > 0 && ms < 86400000 ? ` · ⏳ ${Math.max(1, Math.ceil(ms / 3600000))}h left!` : ""; })()}
                      </p>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}
        {/* 🍕 LEAGUE NIGHT — browsing Local Leagues? Here's where the team eats after the game */}
        {mode === "biz" && cat === "local_leagues" && (() => {
          const food = (businesses || []).filter((b) => b.has_deals && !b.ad_image_url && ["bar_pub", "restaurants_food", "fast_food", "cafe_coffee", "bakery", "liquor"].includes(b.category_id)).slice(0, 12);
          if (!food.length) return null;
          return (
            <div data-testid="league-night-rail" className="mb-2 rounded-xl bg-amber-500/10 border border-amber-400/40 p-2.5">
              <p className="text-[10px] font-heading font-black text-amber-300 mb-1.5">🍕 AFTER THE GAME — food &amp; drink deals near the fields</p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {food.map((x) => (
                  <button key={x.id} data-testid={`league-night-${x.id}`} onClick={() => openWellSquare(x)}
                    className="shrink-0 rounded-lg bg-slate-900/70 border border-amber-400/30 px-2.5 py-1.5 text-left active:scale-95 transition-transform">
                    <p className="text-[11px] font-heading font-black text-white whitespace-nowrap">{x.name}</p>
                    <p className="text-[9px] font-bold text-amber-300">🔥 DEAL INSIDE{x.distance ? ` · ${x.distance} mi` : ""}</p>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}
        {patch && mode === "biz" && (
          <div data-testid="my-patch-stats" className="mb-2 rounded-xl bg-emerald-500/10 border border-emerald-400/40 px-3 py-1.5 flex items-center justify-between gap-2">
            <p className="text-[10px] font-black text-emerald-300 truncate">📊 YOUR PATCH · “{patch.slots[0].headline}”</p>
            <div className="flex items-center gap-2 shrink-0">
              <p className="text-[10px] font-bold text-emerald-200">{patch.slots[0].taps_7d} taps · wall seen {patch.wall_views_7d}× / 7d</p>
              {patch.slots[0].prime ? (
                <span data-testid="patch-prime-badge" className="text-[9px] font-black text-amber-300">👑 PRIME</span>
              ) : (
                <button data-testid="patch-go-prime" onClick={goPrime}
                  className="h-6 px-2 rounded-lg bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 text-[9px] font-black active:scale-95 transition-transform">👑 GO PRIME $3</button>
              )}
            </div>
          </div>
        )}
        {mode === "travel" ? (
          <TravelWall cols={cols} />
        ) : mode === "sports" ? (
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
            {games.length === 0 ? <p className="col-span-full text-center text-sm text-slate-500 mt-10">No games on the wire right now 🏟</p>
              : games.map((g) => <SportCard key={g.id} g={g} />)}
          </div>
        ) : boards === null ? (
          <p className="text-center text-sm text-slate-500 mt-10">Loading the boards…</p>
        ) : boards.length === 0 && well.length === 0 ? (
          <div className="text-center mt-14">
            <p className="text-4xl mb-2">📋</p>
            <p className="font-heading font-black text-slate-300">No ads on this board yet.</p>
            <p className="text-sm text-slate-500 mt-1">Merchants post deals and they appear here instantly.</p>
          </div>
        ) : (
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
            {(() => {
              const cells = boards.map((b) => <BoardCard key={b.business_id} b={b} onExpand={expand} speedMs={speedMs} />);
              cells.splice(Math.min(3, cells.length), 0, <MysteryCard key="mystery" driver={driver} />);
              cells.splice(Math.min(5, cells.length), 0, <TravelCard key="travel" />);
              cells.splice(Math.min(7, cells.length), 0, <RentSlotCard key="rent" driver={driver} />);
              return cells;
            })()}
            {well.map((b) => <MerchantSquare key={`well_${b.id}`} b={b} onOpen={openWellSquare} />)}
          </div>
        )}
      </main>

      {/* 🎛️ SHUFFLE SPEED — POG'R picks the pace of the flipping ads */}
      {mode === "biz" && (
        <button data-testid="board-speed-toggle" onClick={cycleSpeed}
          title="Change how fast the board ads flip"
          className="absolute bottom-6 left-4 z-10 h-12 px-4 rounded-2xl bg-slate-800/90 border border-slate-600/60 text-white font-heading font-black text-xs shadow-lg active:scale-90 transition-transform">
          {(SPEEDS.find((s) => s.ms === speedMs) || SPEEDS[1]).label}
        </button>
      )}

      {/* 🎲 POG SHUFFLE — spin the wall, every merchant gets a fair shot */}
      {mode === "biz" && cat === "random" && (
        <button data-testid="board-shuffle" onClick={shuffle}
          className={`absolute bottom-6 right-4 z-10 h-12 px-4 rounded-2xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-heading font-black text-sm shadow-lg shadow-violet-500/30 active:scale-90 transition-transform ${spinning ? "animate-spin" : ""}`}>
          🎲 {spinning ? "" : "SHUFFLE"}
        </button>
      )}

      {/* 🖼 Fullscreen billboard — tap to expand, minimize back */}
      {full && (
        <div data-testid="board-fullscreen" className="fixed inset-0 z-[1500] bg-black/95 flex flex-col animate-in fade-in duration-200">
          <div className="flex items-center justify-between px-4 pt-[max(0.9rem,env(safe-area-inset-top))]">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-lg bg-white flex items-center justify-center overflow-hidden">
                {logoFor(full, null, BACKEND) ? <img src={logoFor(full, null, BACKEND)} alt="" className="w-7 h-7 object-contain" /> : <span>🏪</span>}
              </span>
              <div>
                <p className="font-heading font-black text-white text-sm">{full.name}</p>
                <p className="text-[10px] text-slate-400 font-bold">{full.badges.join(" ")} {full.category_label || ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button data-testid="board-fullscreen-save" title="Save to POG'KET WALLET ♥" onClick={async () => {
                try {
                  await toggleFavorite(full.business_id, { name: full.name });
                  toast.success(`${full.name} saved to your POG'KET WALLET ♥`);
                } catch (e) {
                  toast.error(e?.response?.status === 401 ? "Sign in to save merchants ♥" : e?.response?.data?.detail || "Couldn't save!");
                }
              }} className="h-9 w-9 rounded-xl bg-slate-800 flex items-center justify-center">
                <span className="text-rose-400 text-base leading-none">♥</span>
              </button>
              <button data-testid="board-fullscreen-close" onClick={() => setFull(null)} className="h-9 w-9 rounded-xl bg-slate-800 flex items-center justify-center">
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
          <div className="flex-1 flex flex-col">
            <AdCarousel ads={full.ads} big speedMs={speedMs} />
          </div>
        </div>
      )}
    </div>
  );
}
